import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import type { PicoAppEnum } from "jadro-pico/apps/PicoAppEnum";
import type { AppAuthState } from "../../../Redux/AppAuthReducer";
import PageLayout from "../Layouts/PageLayout/PageLayout";
import BackHeaderPage from "../Layouts/PageLayout/BackHeaderPage";
import SectionCard from "../SectionCard/SectionCard";
import { SectionCardVariantEnum } from "../SectionCard/SectionCardVariantEnum";
import List from "../List/List";
import ListItem from "../List/ListItem/ListItem";
import ListItemLeadingElement from "../List/ListItem/ListItemLeadingElement";
import Icon from "../Icon/Icon";
import EmptyState from "../EmptyState/EmptyState";
import ErrorState from "../ErrorState/ErrorState";
import Skeleton from "../Skeleton/Skeleton";
import { ThemeColorEnum, ThemeIconEnum } from "../Themes/ThemeEnums";
import { useResource } from "../../../Resources/useResource";
import { RequestQueueResources } from "../../../Resources/RequestQueueResources";
import { useRequestQueue } from "../../../Offline/useRequestQueue";
import type { RequestQueueItem } from "../../../Offline/requestQueueItems";
import { isResolved, useRequestQueueRow } from "./RequestQueueRow";
import { useQueueBack } from "./useQueueBack";
import { useRoutePaths } from "../../../Routes/useRoutePaths";
import { RequestQueueRoutes } from "../../../Routes/RequestQueueRoutes";

const VISIBLE_ROWS = 5;

interface RequestQueueProps {
  apiUrl: string;
  app: PicoAppEnum;
  historyPath: string;
}

const RequestQueue: React.FC<RequestQueueProps> = ({ apiUrl, app, historyPath }) => {
  const resources = useResource(RequestQueueResources);
  const navigate = useNavigate();
  const userUuid = useSelector((s: { auth: AppAuthState }) => s.auth.user?.sub);
  const renderRow = useRequestQueueRow();
  const queuePaths = useRoutePaths(RequestQueueRoutes);
  const goBack = useQueueBack(`${import.meta.env.VITE_PICO_COM_FE_URL}${queuePaths.REQUEST_QUEUE}`);
  const { items, isLoading, error } = useRequestQueue(apiUrl, userUuid ?? "", app);

  const pending = items.filter((item) => !isResolved(item));
  const history = items.filter(isResolved);

  const section = (
    title: string,
    rows: RequestQueueItem[],
    showMore: boolean,
  ): React.ReactElement => (
    <SectionCard variant={SectionCardVariantEnum.SURFACE} title={title} flush>
      <List dividers>
        {(showMore ? rows.slice(0, VISIBLE_ROWS) : rows).map(renderRow)}
        {showMore && (
          <ListItem
            headline={resources.showMore}
            leadingElement={
              <ListItemLeadingElement>
                <Icon icon={ThemeIconEnum.ARROW_FORWARD} color={ThemeColorEnum.PRIMARY} />
              </ListItemLeadingElement>
            }
            onClick={() => void navigate(historyPath)}
          />
        )}
      </List>
    </SectionCard>
  );

  const body = (): React.ReactElement => {
    if (isLoading && items.length === 0) return <Skeleton height={200} />;
    if (error && items.length === 0) return <ErrorState headline={resources.errorHeadline} />;
    if (items.length === 0)
      return (
        <EmptyState
          icon={ThemeIconEnum.CHECK}
          headline={resources.emptyHeadline}
          supporting={resources.emptySupporting}
        />
      );
    return (
      <>
        {pending.length > 0 && section(resources.pendingTitle, pending, false)}
        {history.length > 0 &&
          section(resources.historyTitle, history, history.length > VISIBLE_ROWS)}
      </>
    );
  };

  return (
    <PageLayout maxWidth={720} gap={16} background={ThemeColorEnum.SURFACE_VARIANT}>
      <BackHeaderPage title={resources.title} backLabel={resources.closeAriaLabel} onBack={goBack}>
        {body()}
      </BackHeaderPage>
    </PageLayout>
  );
};

export default RequestQueue;
